import {
  CHAIN_POWER,
  COLOR_BONUS,
  GROUP_BONUS,
  GARBAGE_RATE_1,
  GARBAGE_RATE_2,
  GARBAGE_RATE_3,
  GARBAGE_THRESHOLD_1,
  GARBAGE_THRESHOLD_2,
} from '../constants'
import { Group } from './GroupDetector'

/** chain_power value for a given wave index (0-based). */
function chainPower(waveIndex: number): number {
  if (waveIndex < CHAIN_POWER.length) return CHAIN_POWER[waveIndex]
  return CHAIN_POWER[CHAIN_POWER.length - 1] + (waveIndex - (CHAIN_POWER.length - 1)) * 32
}

/** color_bonus based on distinct colors that popped in this wave. */
function colorBonus(groups: Group[]): number {
  const distinct = new Set(groups.map(g => g.color)).size
  return COLOR_BONUS[Math.min(distinct, 5)]
}

/** Sum of group_bonus for every individual group in this wave. */
function groupBonus(groups: Group[]): number {
  return groups.reduce((sum, g) => {
    const idx = Math.min(g.cells.length, GROUP_BONUS.length - 1)
    return sum + GROUP_BONUS[idx]
  }, 0)
}

/**
 * Score for one chain wave.
 * Formula: (10 × total_beans) × max(1, chain_power + color_bonus + group_bonus)
 */
export function calcWaveScore(waveIndex: number, groups: Group[]): number {
  const totalBeans = groups.reduce((s, g) => s + g.cells.length, 0)
  const multiplier = Math.max(1, chainPower(waveIndex) + colorBonus(groups) + groupBonus(groups))
  return 10 * totalBeans * multiplier
}

/**
 * Convert a raw score into garbage beans with diminishing returns above
 * the two thresholds. The fractional `remainder` carries over between calls
 * so no potential garbage is ever lost.
 *
 * Returns the number of whole garbage beans and the new remainder.
 */
export function scoreToGarbage(
  score: number,
  remainder: number,
): { garbage: number; remainder: number } {
  const total = score + remainder

  let garbage: number
  let newRemainder: number

  if (total <= GARBAGE_THRESHOLD_1) {
    garbage      = Math.floor(total / GARBAGE_RATE_1)
    newRemainder = total % GARBAGE_RATE_1
  } else if (total <= GARBAGE_THRESHOLD_2) {
    const base    = Math.floor(GARBAGE_THRESHOLD_1 / GARBAGE_RATE_1)   // 30
    const excess  = total - GARBAGE_THRESHOLD_1
    garbage      = base + Math.floor(excess / GARBAGE_RATE_2)
    newRemainder = excess % GARBAGE_RATE_2
  } else {
    const base1   = Math.floor(GARBAGE_THRESHOLD_1 / GARBAGE_RATE_1)   // 30
    const base2   = base1 + Math.floor((GARBAGE_THRESHOLD_2 - GARBAGE_THRESHOLD_1) / GARBAGE_RATE_2) // 50
    const excess  = total - GARBAGE_THRESHOLD_2
    garbage      = base2 + Math.floor(excess / GARBAGE_RATE_3)
    newRemainder = excess % GARBAGE_RATE_3
  }

  return { garbage, remainder: newRemainder }
}
